/**
 * Normalizes image URLs to handle cross-fork/deployment compatibility.
 *
 * Converts absolute URLs from old/current preview domains to relative paths
 * so images resolve correctly regardless of which domain serves the app.
 *
 * External CDN URLs (unsplash, etc.) are left untouched.
 * Relative URLs are returned as-is.
 * Empty/invalid inputs return null.
 */
export const normalizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim();
  if (!trimmed) return null;

  try {
    if (trimmed.startsWith('http')) {
      const parsed = new URL(trimmed);

      // Strip domain for any *.preview.emergentagent.com URL → make relative
      if (parsed.hostname.endsWith('.preview.emergentagent.com')) {
        return parsed.pathname + parsed.search;
      }

      // Strip domain for localhost URLs
      if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
        return parsed.pathname + parsed.search;
      }

      // External/CDN URLs (unsplash, cloudinary, etc.) → keep as-is
      return trimmed;
    }

    // Already relative → return as-is
    return trimmed;
  } catch {
    // Malformed URL → return null, let fallback handle it
    return null;
  }
};

/**
 * Normalizes an image URL with a fallback value.
 * Useful for inline usage: normalizeImageOr(url, '/placeholder.png')
 */
export const normalizeImageOr = (url, fallback) => {
  return normalizeImageUrl(url) || fallback || null;
};
